import moment from "moment";
import { IGenericCodebookItem } from "../../codebooks/model/GenericCodebookItem";
import { DateFormats } from "../../core/common/Constants";
import { ILocalizablePresenter } from "../../core/presenter/withStore";
import { TTranslate } from "../../core/service/locale/TranslationService";
import { IOrganisation } from "../../organisations/model/Organisation";
import { IOrganisationIndexGroup } from "../../organisations/model/OrganisationIndexGroup";
import { TSearchQueryRequest } from "../service/SearchService";

export interface TSearchQueryPresentationModel {
  [key: string]: { label: string; description: string; value: string };
}

const SearchQueryPresentationModel = ({
  translate,
  query,
  organisations,
  organisationIndexTree,
  courtCaseTypes,
  caseDecisionTypes
}: {
  translate: TTranslate | ILocalizablePresenter;
  query?: TSearchQueryRequest;
  organisations: IOrganisation[];
  organisationIndexTree: IOrganisationIndexGroup[][];
  courtCaseTypes: IGenericCodebookItem[];
  caseDecisionTypes: IGenericCodebookItem[];
}) => {
  let result = {};
  if (!query) {
    return {};
  }
  Object.keys(query || {}).forEach((key) => {
    let label = translate(`search.query.${key}`);

    switch (key) {
      case "page":
      case "pageSize":
        break;
      case "phrase":
      case "q":
        result[key] = {
          label: label,
          value: query[key],
          description: query[key] || translate("search.query.all_items")
        };
        break;
      case "court_case_number_ss":
      case "court_case_description_ss":
        result[key] = {
          label: label,
          value: query[key],
          description: query[key]
        };
        break;
      case "court_case_type_ss":
        result[key] = {
          label: label,
          value: query[key],
          description: (query[key] || "")
            .split(",")
            .map(
              (item: string) =>
                courtCaseTypes?.find(
                  (codebookItem) => codebookItem.id.toString() === item
                )?.value
            )
            .join(", ")
        };
        break;
      case "case_decision_type_ss":
        result[key] = {
          label: label,
          value: query[key],
          description: (query[key] || "")
            .split(",")
            .map(
              (item: string) =>
                caseDecisionTypes?.find(
                  (codebookItem) => codebookItem.id.toString() === item
                )?.value
            )
            .join(", ")
        };
        break;
      case "organisation":
      case "organisation_id":
        result[key] = {
          label: label,
          value: query[key],
          description: (query[key] || "")
            .split(",")
            .map(
              (item) =>
                organisations.find((item2) => item2.id.toString() === item)
                  ?.name
            )
            .join(", ")
        };
        break;
      case "collection":
        result[key] = {
          label: label,
          value: query[key],
          description: (query[key] || "")
            .split(",")
            .map((item: string) =>
              translate(`search.query.collection_${item.toLowerCase()}`)
            )
            .join(", ")
        };
        break;
      case "index":
        result[key] = {
          label: label,
          value: query[key],
          description: (query[key] || "")
            .split(",")
            .map((item: string) => {
              let id = Number.parseInt(item, 10);
              for (var tree of organisationIndexTree) {
                for (let group of tree) {
                  let result: any = group.find(id);
                  if (result) {
                    return result.name;
                  }
                  result = group.findIndex(id);
                  if (result) {
                    return result.name;
                  }
                }
              }
            })
            .join(", ")
        };
        break;
      case "date_dt":
      case "decision_date_dt":
        let dates: string[] = (query[key] || "").split(",");
        if (dates[0] === dates[1]) {
          dates.pop();
        }
        result[key] = {
          label: label,
          value: query[key],
          description: dates
            .map((item: string) =>
              moment(item, DateFormats.SEARCH_DATE).format(DateFormats.DATE)
            )
            .join(" - ")
        };
        break;
    }
  });

  if (
    query["summary"] ||
    query["notes_txt"] ||
    query["attr_attachment_content"]
  ) {
    let value =
      query["summary"] ||
      query["notes_txt"] ||
      query["attr_attachment_content"];
    let fields: string[] = [];
    if (query["summary"]) {
      fields.push(translate(`search.query.summary`));
    }
    if (query["notes_txt"]) {
      fields.push(translate(`search.query.notes_txt`));
    }
    if (query["attr_attachment_content"]) {
      fields.push(translate(`search.query.attr_attachment_content`));
    }
    let description = value;
    switch (fields.length) {
      case 1:
        description += ` ${fields[0]}`;
        break;
      case 2:
        description += ` ${fields.join(translate("search.query.and"))}`;
        break;
      case 3:
        description += ` ${fields[0]}, ${fields[1]}${translate(
          "search.query.and"
        )}${fields[2]}`;
    }
    result["phrase"] = {
      label: translate(`search.query.phrase`),
      value: value,
      description: description
    };
  }
  return result as TSearchQueryPresentationModel;
};

export default SearchQueryPresentationModel;
